import { MATH_CONSTANTS } from './constants.js';
import { getColorPattern } from './config.js';
import { calculateTreeBounds, calculateAutoFitTransform } from './export-utils.js';

/**
 * SVG renderer for exporting Pythagoras Trees as vector graphics
 */

/**
 * SVG renderer for exporting Pythagoras Trees as scalable vector graphics
 */
export class SVGRenderer {
    constructor(config = {}) {
        this.config = config;
    }

    /**
     * Export complete fractal tree as SVG
     * @param {Array} squares - Array of Square objects
     * @param {Array} triangles - Array of Triangle objects (optional)
     * @param {number} width - SVG viewport width
     * @param {number} height - SVG viewport height
     * @param {Object} options - Export options
     * @returns {string} SVG markup
     */
    exportTreeAsSVG(squares, triangles = [], width = 1024, height = 1024, options = {}) {
        const opts = {
            precision: 2,
            includeBackground: true,
            optimize: true,
            padding: 50,
            centerTree: true,
            ...options,
        };

        // Calculate tree bounds for auto-fitting
        const bounds = calculateTreeBounds(squares, triangles);
        const transform = calculateAutoFitTransform(bounds, width, height, opts.padding);

        // Create SVG document
        let svgContent = this.createSVGDocument(width, height, opts);

        // Add background if requested
        if (opts.includeBackground && this.config.backgroundColor) {
            svgContent += this.createBackgroundRect(width, height, this.config.backgroundColor);
        }

        // Get color pattern
        const pattern = getColorPattern(this.config.colorPattern) || getColorPattern('classic');
        const maxLevel = squares.length > 0 ? Math.max(...squares.map(s => s.level)) : 0;

        // Render squares
        if (pattern.showSquares && pattern.renderSquares) {
            for (const square of squares) {
                const color = pattern.renderSquares(
                    square,
                    this.config,
                    maxLevel,
                    squares,
                    triangles
                );
                if (color) {
                    if (typeof color === 'string') {
                        svgContent += this.drawSquareToSVG(
                            square,
                            color,
                            transform,
                            opts.precision
                        );
                    } else if (typeof color === 'object' && color.stroke) {
                        svgContent += this.drawSquareToSVG(
                            square,
                            color,
                            transform,
                            opts.precision
                        );
                    }
                }
            }
        }

        // Render triangles
        if (pattern.showTriangles && pattern.renderTriangles && triangles.length > 0) {
            for (const triangle of triangles) {
                const color = pattern.renderTriangles(
                    triangle,
                    this.config,
                    maxLevel,
                    squares,
                    triangles
                );
                if (color) {
                    if (typeof color === 'string') {
                        svgContent += this.drawTriangleToSVG(
                            triangle,
                            color,
                            transform,
                            opts.precision
                        );
                    } else if (typeof color === 'object' && color.stroke) {
                        svgContent += this.drawTriangleToSVG(
                            triangle,
                            color,
                            transform,
                            opts.precision
                        );
                    }
                }
            }
        }

        // Close SVG document
        svgContent += '</svg>';

        return opts.optimize ? this.optimizeSVG(svgContent) : svgContent;
    }

    /**
     * Create SVG document header
     * @param {number} width - SVG width
     * @param {number} height - SVG height
     * @param {Object} options - SVG options
     * @returns {string} SVG header
     */
    createSVGDocument(width, height) {
        const bgColor = this.config.backgroundColor || '#FFFFFF';
        return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" 
     width="${width}" 
     height="${height}" 
     viewBox="0 0 ${width} ${height}">
  <defs>
    <style>
      .svg-background { fill: ${bgColor}; }
    </style>
  </defs>
  <rect class="svg-background" width="100%" height="100%"/>
`;
    }

    /**
     * Create background rectangle
     * @param {number} width - Width
     * @param {number} height - Height
     * @param {string} color - Background color
     * @returns {string} SVG rect element
     */
    createBackgroundRect(width, height, color) {
        return `  <rect x="0" y="0" width="${width}" height="${height}" fill="${color}"/>\n`;
    }

    /**
     * Convert square to SVG rect element
     * @param {Square} square - Square object
     * @param {string} color - Fill color
     * @param {Object} transform - Transformation parameters
     * @param {number} precision - Coordinate precision
     * @returns {string} SVG rect element
     */ drawSquareToSVG(square, color, transform, precision) {
        // Apply transformation to center coordinates
        const centerX = this.round((square.x + transform.offsetX) * transform.scale, precision);
        const centerY = this.round((square.y + transform.offsetY) * transform.scale, precision);
        const size = this.round(square.size * transform.scale, precision);

        // Convert angle from radians to degrees
        const angle = this.round(square.angle * MATH_CONSTANTS.RADIANS_TO_DEGREES, precision);

        // Calculate top-left position for SVG rect (SVG rects are positioned by top-left corner)
        const halfSize = size / 2;
        const x = this.round(centerX - halfSize, precision);
        const y = this.round(centerY - halfSize, precision);

        let svgRect;

        if (typeof color === 'string') {
            // Fill mode
            svgRect = `  <rect x="${x}" y="${y}" width="${size}" height="${size}" fill="${color}"`;
            // Add black border if drawBorder is enabled
            if (this.config.drawBorder) {
                svgRect += ` stroke="#000000" stroke-width="1"`;
            }
        } else if (typeof color === 'object' && color.stroke) {
            if (color.edges && Array.isArray(color.edges)) {
                // Selective edge drawing mode (for outline picture)
                return this.drawSquareEdgesToSVG(square, color, transform, precision);
            } else {
                // Full outline mode
                const strokeWidth = color.lineWidth || 1;
                svgRect = `  <rect x="${x}" y="${y}" width="${size}" height="${size}" fill="none" stroke="${color.stroke}" stroke-width="${strokeWidth}"`;
            }
        } else {
            return '';
        }

        // Add rotation if needed
        if (Math.abs(angle) > MATH_CONSTANTS.ANGLE_TOLERANCE) {
            svgRect += ` transform="rotate(${angle} ${centerX} ${centerY})"`;
        }

        svgRect += '/>\n';
        return svgRect;
    }

    /**
     * Convert triangle to SVG polygon element
     * @param {Triangle} triangle - Triangle object
     * @param {string} color - Fill color
     * @param {Object} transform - Transformation parameters
     * @param {number} precision - Coordinate precision
     * @returns {string} SVG polygon element
     */
    drawTriangleToSVG(triangle, color, transform, precision) {
        if (!triangle.vertices || triangle.vertices.length < 3) {
            return '';
        }

        const points = triangle.vertices
            .map(vertex => {
                const x = this.round((vertex.x + transform.offsetX) * transform.scale, precision);
                const y = this.round((vertex.y + transform.offsetY) * transform.scale, precision);
                return `${x},${y}`;
            })
            .join(' ');

        if (typeof color === 'string') {
            // Fill mode
            let polygonElement = `  <polygon points="${points}" fill="${color}"`;
            // Add black border if drawBorder is enabled
            if (this.config.drawBorder) {
                polygonElement += ` stroke="#000000" stroke-width="1"`;
            }
            polygonElement += `/>\n`;
            return polygonElement;
        } else if (typeof color === 'object' && color.stroke) {
            // Outline mode
            const strokeWidth = color.lineWidth || 1;
            return `  <polygon points="${points}" fill="none" stroke="${color.stroke}" stroke-width="${strokeWidth}"/>\n`;
        }

        return '';
    }

    /**
     * Draw specific edges of a square as SVG path elements
     * @param {Square} square - Square object
     * @param {Object} color - Color object with stroke, lineWidth, and edges properties
     * @param {Object} transform - Transformation parameters
     * @param {number} precision - Coordinate precision
     * @returns {string} SVG path elements
     */
    drawSquareEdgesToSVG(square, color, transform, precision) {
        const centerX = this.round((square.x + transform.offsetX) * transform.scale, precision);
        const centerY = this.round((square.y + transform.offsetY) * transform.scale, precision);
        const size = this.round(square.size * transform.scale, precision);
        const angle = this.round((square.angle * 180) / Math.PI, precision);
        const strokeWidth = color.lineWidth || 1;
        const halfSize = size / 2;

        // Define the four corners of the square
        const corners = {
            topLeft: { x: centerX - halfSize, y: centerY - halfSize },
            topRight: { x: centerX + halfSize, y: centerY - halfSize },
            bottomRight: { x: centerX + halfSize, y: centerY + halfSize },
            bottomLeft: { x: centerX - halfSize, y: centerY + halfSize },
        };

        let svgPaths = '';
        const edges = color.edges || ['top', 'right', 'bottom', 'left'];

        // Generate path for each requested edge
        edges.forEach(edge => {
            let pathData = '';

            switch (edge) {
                case 'top':
                    pathData = `M ${corners.topLeft.x} ${corners.topLeft.y} L ${corners.topRight.x} ${corners.topRight.y}`;
                    break;
                case 'right':
                    pathData = `M ${corners.topRight.x} ${corners.topRight.y} L ${corners.bottomRight.x} ${corners.bottomRight.y}`;
                    break;
                case 'bottom':
                    pathData = `M ${corners.bottomRight.x} ${corners.bottomRight.y} L ${corners.bottomLeft.x} ${corners.bottomLeft.y}`;
                    break;
                case 'left':
                    pathData = `M ${corners.bottomLeft.x} ${corners.bottomLeft.y} L ${corners.topLeft.x} ${corners.topLeft.y}`;
                    break;
            }

            if (pathData) {
                let pathElement = `  <path d="${pathData}" fill="none" stroke="${color.stroke}" stroke-width="${strokeWidth}"`;

                // Add rotation if needed
                if (Math.abs(angle) > 0.01) {
                    pathElement += ` transform="rotate(${angle} ${centerX} ${centerY})"`;
                }

                pathElement += '/>\n';
                svgPaths += pathElement;
            }
        });

        return svgPaths;
    }

    /**
     * Optimize SVG content by removing unnecessary whitespace and rounding
     * @param {string} svgContent - SVG markup
     * @returns {string} Optimized SVG
     */
    optimizeSVG(svgContent) {
        return svgContent
            .replace(/\s+/g, ' ') // Normalize whitespace
            .replace(/> </g, '><') // Remove spaces between tags
            .replace(/\n\s*/g, '\n') // Clean up line breaks
            .trim();
    }

    /**
     * Round number to specified precision
     * @param {number} value - Value to round
     * @param {number} precision - Decimal places
     * @returns {number} Rounded value
     */
    round(value, precision) {
        const factor = Math.pow(10, precision);
        return Math.round(value * factor) / factor;
    }

    /**
     * Generate filename for SVG export
     * @param {Object} config - Configuration object
     * @returns {string} Filename
     */
    generateFilename(config) {
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        const pattern = config.colorPattern || 'classic';
        return `pythagoras-tree-${pattern}-${timestamp}.svg`;
    }
}
